/**
 * Orders Table
 *
 * The default Orders view: the ongoing history of every order, from Placed
 * through to Delivered / Cancelled / Refunded.
 *
 * Columns: Order ID · Store · Customer · Created · Amount · Status · Action
 */

import React, { useState } from 'react';
import { Box, Chip, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useListContext, useNotify } from 'react-admin';
import { printDocument, downloadDocument, runDocumentAction } from '../../../utils/orderDocuments';
import { useHUDTheme } from '../theme';
import { FONT_DATA } from '../../../theme/grounds';
import { monoLabelSx } from '../../reskin/primitives';
import { STATUS_META, statusKeyOf } from '../statusMeta';
import type { Order } from '../context/HUDContext';

/**
 * Columns the API will sort by. Anything absent stays a plain header.
 *
 * Sorting goes through react-admin's `setSort`, which the data provider turns
 * into `sortBy`/`sortOrder` — so it sorts the whole result set, not the loaded
 * page.
 *
 * Deliberately only Created and Status — the two axes this history is actually
 * read along. The rest are left unsortable on purpose:
 *
 * - Order ID and Amount *could* sort (`number` and `total` are both in the
 *   controller's allowlist) but an order list ranked by id or by value isn't a
 *   view anyone works from, so the handles were removed.
 * - Store and Customer have no server-side counterpart in that allowlist, and
 *   sorting them in the browser would silently reorder only the loaded page.
 *   To narrow to one store, use the store picker in the app bar.
 * - Action is a button column with nothing to order by.
 */
const SORTABLE: Record<string, string> = {
  Created: 'createdAt',
  Status: 'status',
};

interface OrdersTableProps {
  orders: Order[];
  highlightedOrderIds: Set<string>;
  /** Opens the right-hand preview drawer. */
  onView: (order: Order) => void;
}

const money = (n: number) =>
  `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Date over time, in SAST — the list is history, so the absolute stamp matters. */
function CreatedCell({ iso }: { iso: string }) {
  const { hud } = useHUDTheme();
  if (!iso) return <Box sx={{ color: hud.textMuted }}>—</Box>;
  const d = new Date(iso);
  return (
    <Box sx={{ fontFamily: FONT_DATA, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: hud.textPrimary }}>
      {d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
      <Box component="span" sx={{ display: 'inline-block', width: '1.4em' }} />
      {d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })}
    </Box>
  );
}

export default function OrdersTable({ orders, highlightedOrderIds, onView }: OrdersTableProps) {
  const { hud } = useHUDTheme();
  const { sort, setSort } = useListContext();
  const notify = useNotify();

  /** Which row's print menu is open. */
  const [printFor, setPrintFor] = useState<{ el: HTMLElement; order: Order } | null>(null);

  const closePrint = () => setPrintFor(null);

  // Printing goes straight to the browser's dialog; the PDFs are for filing.
  // Both of these used to be wrong: "packing slip" fetched the label, and the
  // label item called the UBER route with a Deeliver order id, which 404s and
  // was saved as a corrupt .pdf.
  const runFor = (run: (id: string, number: string) => Promise<void>) => () => {
    if (printFor) runDocumentAction(run(printFor.order.id, printFor.order.number), notify);
    closePrint();
  };

  const printSlip = runFor((id) => printDocument('slip', id));
  const printLabel = runFor((id) => printDocument('label', id));
  const downloadSlip = runFor((id, number) => downloadDocument('slip', id, `${number} Order Slip.pdf`));
  const downloadLabel = runFor((id, number) => downloadDocument('label', id, `${number} Label.pdf`));

  if (!orders.length) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', color: hud.textMuted }}>
        No orders match this view.
      </Box>
    );
  }

  const headCell = {
    ...monoLabelSx,
    textAlign: 'left' as const,
    padding: '10px 14px',
    fontSize: '0.66rem',
    letterSpacing: '0.12em',
    color: hud.textMuted,
    bgcolor: 'var(--sunk)',
    borderBottom: `1px solid ${hud.border}`,
    whiteSpace: 'nowrap' as const,
  };

  /**
   * Header cell — clickable when the API can sort by it. First click sorts
   * descending (newest / biggest first, which is what you nearly always want
   * of orders), and clicking the active column again flips it.
   */
  const Head = ({ label, align = 'left' }: { label: string; align?: 'left' | 'right' }) => {
    const field = SORTABLE[label];
    const active = !!field && sort?.field === field;
    const descending = active && sort?.order === 'DESC';

    const sx = { ...headCell, textAlign: align, ...(field ? { cursor: 'pointer', userSelect: 'none' as const } : {}) };

    return (
      <Box
        component="th"
        sx={{ ...sx, ...(active ? { color: hud.accent } : {}), '&:hover': field ? { color: hud.textPrimary } : undefined }}
        onClick={field ? () => setSort({ field, order: active && descending ? 'ASC' : 'DESC' }) : undefined}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            flexDirection: align === 'right' ? 'row-reverse' : 'row',
          }}
        >
          {label}
          {active && (
            <ArrowUpwardIcon
              sx={{
                fontSize: 14,
                transition: 'transform .15s',
                transform: descending ? 'rotate(180deg)' : 'none',
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ border: `1px solid ${hud.border}`, borderRadius: '8px', overflow: 'auto', bgcolor: hud.elevated }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <Box component="thead">
          <Box component="tr">
            <Head label="Order ID" />
            <Head label="Store" />
            <Head label="Customer" />
            <Head label="Created" />
            <Head label="Amount" align="right" />
            <Head label="Status" />
            <Head label="Action" align="right" />
          </Box>
        </Box>

        <Box component="tbody">
          {orders.map((order) => {
            const meta = STATUS_META[statusKeyOf(order)];
            const highlighted = highlightedOrderIds.has(order.id);
            const customer = `${order.user?.firstName ?? ''} ${order.user?.lastName ?? ''}`.trim();

            const cell = {
              padding: '11px 14px',
              borderBottom: `1px solid ${hud.borderLight}`,
              color: hud.textSecondary,
              verticalAlign: 'middle' as const,
            };

            return (
              <Box
                component="tr"
                key={order.id}
                onClick={() => onView(order)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: highlighted ? `${hud.accent}14` : 'transparent',
                  transition: 'background .12s',
                  '&:hover': { bgcolor: hud.hover },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <Box component="td" sx={{ ...cell, fontWeight: 700, fontFamily: FONT_DATA, color: hud.textPrimary, whiteSpace: 'nowrap' }}>
                  #{order.number}
                </Box>

                <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                  {order.store?.name || order.storeId}
                </Box>

                <Box component="td" sx={cell}>
                  <Box sx={{ fontWeight: 600, color: hud.textPrimary }}>{customer || 'Customer'}</Box>
                  {order.user?.phoneNumber && (
                    <Box sx={{ fontSize: '0.75rem', color: hud.textMuted }}>{order.user.phoneNumber}</Box>
                  )}
                </Box>

                <Box component="td" sx={cell}>
                  <CreatedCell iso={order.createdAt} />
                </Box>

                <Box component="td" sx={{ ...cell, textAlign: 'right', fontFamily: FONT_DATA, fontVariantNumeric: 'tabular-nums', color: hud.textPrimary, whiteSpace: 'nowrap' }}>
                  {money(order.total)}
                </Box>

                <Box component="td" sx={cell}>
                  <Box
                    component="span"
                    title={order.status}
                    sx={{
                      ...monoLabelSx,
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      px: '9px', py: '2px', borderRadius: '11px',
                      fontSize: '0.66rem', letterSpacing: '0.1em', whiteSpace: 'nowrap',
                      color: meta.color,
                      bgcolor: `${meta.color}1f`,
                      border: `1px solid ${meta.color}55`,
                    }}
                  >
                    <Box component="i" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flex: 'none' }} />
                    {meta.label}
                  </Box>
                  {/* A cancelled/returned order whose Vend sale is still live. Voiding is
                      a manual action, so this stays visible until someone does it —
                      otherwise the POS keeps revenue + a stock decrement for goods that
                      came back, silently. */}
                  {['cancelled', 'returned'].includes(order.status)
                    && order.vendSaleId
                    && !order.vendVoidedAt && (
                    <Tooltip title={order.returnedToStoreAt
                      ? 'Goods returned to the store — void the sale to put the stock back'
                      : 'Vend sale is still live — needs voiding'}>
                      <Chip
                        size="small"
                        label="VOID?"
                        sx={{
                          ml: 0.75,
                          bgcolor: `${hud.warning}22`,
                          color: hud.warning,
                          border: `1px solid ${hud.warning}55`,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                        }}
                      />
                    </Tooltip>
                  )}
                  {/* Once voided, say so: a VOID? chip that just vanishes reads the same as
                      a sale that never needed voiding. */}
                  {order.vendSaleId && order.vendVoidedAt && (
                    <Tooltip title={`Vend sale voided ${new Date(order.vendVoidedAt).toLocaleString()}`}>
                      <Chip
                        size="small"
                        label="VOIDED"
                        sx={{
                          ml: 0.75,
                          bgcolor: `${hud.textSecondary}20`,
                          color: hud.textSecondary,
                          border: `1px solid ${hud.textSecondary}55`,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                <Box
                  component="td"
                  sx={{ ...cell, textAlign: 'right' }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Tooltip title="View order">
                    <IconButton size="small" onClick={() => onView(order)} sx={{ color: hud.textSecondary }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton
                      size="small"
                      onClick={(e) => setPrintFor({ el: e.currentTarget, order })}
                      sx={{ color: hud.textSecondary }}
                    >
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Print menu — one per table, anchored to whichever row was clicked. */}
      <Menu anchorEl={printFor?.el ?? null} open={!!printFor} onClose={closePrint}>
        <MenuItem onClick={printSlip}>
          <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Print Slip</ListItemText>
        </MenuItem>
        <MenuItem onClick={printLabel}>
          <ListItemIcon><LocalOfferIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Print Label</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={downloadSlip}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download slip PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={downloadLabel}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download label PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
